import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { CREATE_ADFS, GET_ADFS } from './ADFSConfig.graphql';
import { Edit } from '@mui/icons-material';
import { AdfsSetting } from '../../interfaces';
import { Link } from 'react-router-dom';
import { GET_LICENSE } from '../../licenses/details/LicenseInfo.graphql';
import { useQuery } from '@apollo/client/react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Grid, ListItemSecondaryAction, Stack } from '@mui/material';
import { Close } from '@mui/icons-material';
import SchemeField from '../../sso/SchemeField';
import { schemeFromCompanyName } from '../../sso/scheme';
import EmailDomain from './EmailDomain';
import CallbackPathField from '../../sso/CallbackPathField';
import { ALL_IDENTITY_PROVIDERS, GET_IDENTITY_BASE_URL } from '../../sso/Sso.graphql';
import LicensePicker, { PickedLicense } from '../../components/LicensePicker';
import TenantID from './TenantID';
import ClientID from './ClientID';
import ClientSecret from './ClientSecret';

interface Props {
  licenseId: number;
  licenseScheme: string;
  companyName?: string | null;
}
export default function NewADFSButton({ licenseId, licenseScheme, companyName }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Grid container direction="row" sx={{alignItems: 'flex-start'}}>
        {!licenseScheme && (
          <ListItemSecondaryAction>
            <IconButton onClick={() => setOpen(true)} title="Endre til firmapålogging" size="large">
              <Edit />
            </IconButton>
          </ListItemSecondaryAction>
        )}
        {licenseScheme && (
          <>
            <Grid size={10}>
              <Link
                to={`/license/${licenseId}/idp`}
                style={{
                  fontSize: 16,
                  fontStyle: 'selectorRoboto',
                  fontWeight: 400,
                  color: '#0000ee',
                }}>
                Firmapålogging · {licenseScheme}
              </Link>
            </Grid>
          </>
        )}
      </Grid>
      {open && licenseId && <AddADFSConfigDialog licenseId={licenseId} companyName={companyName} onClose={() => setOpen(false)} />}
    </>
  );
}
interface DialogProps {
  licenseId?: number;
  companyName?: string | null;
  takenLicenseIds?: ReadonlySet<number>;
  onClose: () => void;
  onCreated?: (licenseId: number) => void;
}
export function AddADFSConfigDialog({ licenseId: givenLicenseId, companyName: givenCompanyName, takenLicenseIds, onClose, onCreated }: DialogProps) {
  const [picked, setPicked] = useState<PickedLicense | null>(null);
  const licenseId = givenLicenseId ?? picked?.id;
  const companyName = givenLicenseId ? givenCompanyName : picked?.companyName;
  const [tenantID, setTenantID] = useState('');
  const [clientID, setClientID] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [providerName, setProviderName] = useState('');
  const [callbackPath, setCallbackPath] = useState('');
  const [isErrorOnProviderName, setIsErrorOnProviderName] = useState(false);
  const [schemeTouched, setSchemeTouched] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isErrorOnEmailDomain, setIsErrorOnEmailDomain] = useState(false);
  const [isErrorOnCallbackPath, setIsErrorOnCallbackPath] = useState(false);
  const { data: identityData } = useQuery(GET_IDENTITY_BASE_URL);

  const handleClose = () => {
    setTenantID('');
    setClientID('');
    setClientSecret('');
    setProviderName('');
    setCallbackPath('');
    onClose();
  };
  const handleCloseBtn = () => {
    if (!loading) handleClose();
  };
  const isFormValid = () => {
    return licenseId && tenantID && clientID && clientSecret && providerName && emailDomain && callbackPath && !isErrorOnCallbackPath && !isErrorOnProviderName && !isErrorOnEmailDomain;
  };
  const [create, { loading }] = useMutation<any, any>(CREATE_ADFS, {
    variables: {
      input: {
        tenantID: tenantID,
        clientID: clientID,
        clientSecret: clientSecret,
        providerID: providerName,
        emailDomains: emailDomain,
        callbackPath: callbackPath,
        bksID: licenseId,
      },
    },
    refetchQueries: [
      {
        query: GET_LICENSE,
        variables: { id: licenseId },
      },
      {
        query: GET_ADFS,
      },
      { query: ALL_IDENTITY_PROVIDERS },
    ],
  });
  const allAdfs = useQuery<{ adfs: AdfsSetting[] }>(GET_ADFS);
  const result = allAdfs && allAdfs.data && allAdfs.data.adfs;
  useEffect(() => {
    if (!schemeTouched && result) setProviderName(schemeFromCompanyName(companyName, result.map((x) => x.scheme)));
  }, [result, companyName, schemeTouched]);
  const callbackProviderNameError = (errorStatus: boolean) => {
    setIsErrorOnProviderName(errorStatus);
  };
  const callbackEmailDomain = (domain: string) => {
    setEmailDomain(domain);
  };
  const callbackEmailDomainError = (errorStatus: boolean) => {
    setIsErrorOnEmailDomain(errorStatus);
  };
  const callbackCallbackPath = (path: string) => {
    setCallbackPath(path);
  };
  const callbackCallbackPathError = (errorStatus: boolean) => {
    setIsErrorOnCallbackPath(errorStatus);
  };
  const callbackTenantID = (tenantID: string) => {
    setTenantID(tenantID);
  };
  const callbackClientID = (clientID: string) => {
    setClientID(clientID);
  };
  const callbackClientSecret = (clientSecret: string) => {
    setClientSecret(clientSecret);
  };
  const handleSave = async () => {
    if (!isErrorOnProviderName && !isErrorOnEmailDomain && !isErrorOnCallbackPath) {
      setSaveError(null);
      try {
        await create();
        handleClose();
        onCreated?.(licenseId!);
      } catch (e: any) {
        setSaveError(e?.message ?? 'Kunne ikke sette opp firmapåloggingen.');
      }
    }
  };
  return (
    <Dialog fullWidth maxWidth="md" onClose={handleCloseBtn} open={true}>
      <DialogTitle>
        Ny firmapålogging
        <IconButton
          aria-label="close"
          sx={{
            position: 'absolute',
            right: (theme) => theme.spacing(1),
            top: (theme) => theme.spacing(1),
            color: (theme) => theme.palette.grey[500],
          }}
          disabled={loading}
          onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Stack spacing sets the gap above each field; their own bottom margin would add to it. */}
        <Stack spacing={2} sx={{ '& .MuiFormControl-marginNormal': { mb: 0 } }}>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          {!givenLicenseId && (
            <LicensePicker
              value={picked}
              onChange={setPicked}
              unavailable={takenLicenseIds && { ids: takenLicenseIds, reason: 'har firmapålogging' }}
              disabled={loading}
              autoFocus
            />
          )}
          <SchemeField
            value={providerName}
            onChange={(value) => {
              setSchemeTouched(true);
              setProviderName(value);
            }}
            onValidity={(valid) => callbackProviderNameError(!valid)}
            otherSchemes={(result ?? []).map((x) => x.scheme)}
            helperText="Foreslått fra firmanavnet. Kan ikke endres etter oppsett, fordi Identity og portalen kjenner leverandøren under den."
          />
          <TenantID callbackValue={callbackTenantID} existingTenant={''} />
          <ClientID callbackValue={callbackClientID} existingId={''} />
          <ClientSecret callbackValue={callbackClientSecret} />
          <EmailDomain result={result} callbackValue={callbackEmailDomain} callbackError={callbackEmailDomainError} id={''} domain={[]} />
          <CallbackPathField
            scheme={providerName}
            initialPath=""
            otherPaths={(result ?? []).map((x) => x.callbackPath ?? '')}
            identityBaseUrl={identityData?.identityBaseUrl}
            onChange={(path, valid) => {
              callbackCallbackPath(path);
              callbackCallbackPathError(!valid);
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" disabled={loading} onClick={() => handleClose()}>
          Avbryt
        </Button>
        <Button color="primary" variant="contained" loading={loading} disabled={!isFormValid()} onClick={() => handleSave()}>
          Opprett
        </Button>
      </DialogActions>
    </Dialog>
  );
}
